import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightweight client-side pager. Given an array, returns the current page +
 * controls. Use in admin listings that aren't paginated server-side.
 *
 *   const pager = usePager(items, 20);
 *   <Pager {...pager} />
 *   {pager.pageItems.map(...)}
 */
export function usePager(items, perPage = 20) {
  const [page, setPage] = React.useState(1);
  const total = items?.length || 0;
  const pages = Math.max(1, Math.ceil(total / perPage));
  React.useEffect(() => { if (page > pages) setPage(1); }, [pages, page]);
  const start = (page - 1) * perPage;
  const pageItems = (items || []).slice(start, start + perPage);
  return { page, setPage, perPage, total, pages, pageItems, start };
}

export default function Pager({ page, setPage, total, pages, perPage, start, label = "items", className = "", testId = "pager" }) {
  if (total <= perPage) return null;
  const end = Math.min(start + perPage, total);
  return (
    <div className={`flex items-center justify-between gap-3 mt-6 ${className}`} data-testid={testId}>
      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
        Showing {start + 1}–{end} of {total} {label}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="kb-btn-outline text-xs disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2"
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Prev
        </button>
        <span className="kb-mono text-[10px] text-kb-ink px-2">{page} / {pages}</span>
        <button
          onClick={() => setPage(Math.min(pages, page + 1))}
          disabled={page === pages}
          className="kb-btn-outline text-xs disabled:opacity-40 disabled:cursor-not-allowed px-3 py-2"
          data-testid={`${testId}-next`}
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
